'use client';

import { useCallback, useEffect, useState } from 'react';
import { AccountDialog, type AccountTab } from '@/components/Account';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthRequiredAction } from '@/hooks/useAuthRequiredAction';
import { appDialogBus, type OpenAlertDialogDetail } from '@/lib/app-dialog-bus';

export function GlobalAppDialogs() {
  const { t } = useTranslation();
  const { runIfAuthed } = useAuthRequiredAction();
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [accountDialogTab, setAccountDialogTab] = useState<AccountTab>('recharge');
  const [alertDialogState, setAlertDialogState] = useState<OpenAlertDialogDetail | null>(null);

  useEffect(() => {
    const handleOpenAccountDialog = (event: Event) => {
      const detail = (event as CustomEvent<{ tab?: AccountTab }>).detail;
      const tab = detail?.tab ?? 'recharge';

      // Account dialogs expose user/balance data, so gate them behind login.
      // When logged out, defer the open intent until after login succeeds.
      runIfAuthed(() => {
        setAccountDialogTab(tab);
        setAccountDialogOpen(true);
      });
    };

    const handleOpenAlertDialog = (event: Event) => {
      const detail = (event as CustomEvent<OpenAlertDialogDetail>).detail;
      setAlertDialogState({
        title: detail?.title ?? '',
        description: detail?.description ?? '',
        confirmLabel: detail?.confirmLabel ?? t('common.confirm'),
        cancelLabel: detail?.cancelLabel ?? t('common.cancel'),
        size: detail?.size ?? 'sm',
        contentClassName: detail?.contentClassName,
        onConfirm: detail?.onConfirm,
      });
    };

    appDialogBus.onOpenAccountDialog(handleOpenAccountDialog);
    appDialogBus.onOpenAlertDialog(handleOpenAlertDialog);

    return () => {
      appDialogBus.offOpenAccountDialog(handleOpenAccountDialog);
      appDialogBus.offOpenAlertDialog(handleOpenAlertDialog);
    };
  }, [runIfAuthed, t]);

  const handleAlertDialogOpenChange = (open: boolean) => {
    if (!open) {
      setAlertDialogState(null);
    }
  };

  const handleAlertDialogAction = useCallback(() => {
    const onConfirm = alertDialogState?.onConfirm;
    setAlertDialogState(null);
    onConfirm?.();
  }, [alertDialogState]);

  return (
    <>
      <AlertDialog open={alertDialogState !== null} onOpenChange={handleAlertDialogOpenChange}>
        <AlertDialogContent
          size={alertDialogState?.size ?? 'sm'}
          className={alertDialogState?.contentClassName}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{alertDialogState?.title ?? ''}</AlertDialogTitle>
            <AlertDialogDescription>{alertDialogState?.description ?? ''}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{alertDialogState?.cancelLabel ?? t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAlertDialogAction}
              className="shadow-none hover:shadow-none"
            >
              {alertDialogState?.confirmLabel ?? t('common.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AccountDialog
        open={accountDialogOpen}
        onOpenChange={setAccountDialogOpen}
        defaultTab={accountDialogTab}
      />
    </>
  );
}
